/**
 * MANAGER_TERMINAL — live branch overview + approval inbox.
 *
 * The overview is composed from the floor map, today's orders, and per-station
 * load — enough to answer the three questions a shift leader actually asks
 * ("is the kitchen drowning", "which tables are stuck", "what have we taken
 * today").
 *
 * The approval inbox is `GET /v1/approvals` (routes/approvals.ts): a UNIFIED
 * read over every pending domain row (ứng lương, xin nghỉ, đổi ca, kiểm kê,
 * đơn mua hàng, hao hụt, khiếu nại KPI…), already filtered server-side to the
 * items THIS manager holds the type-specific permission to decide. Both
 * approve and reject REQUIRE a reason (`x-action-reason`), and the server
 * refuses self-approval outright (segregation of duties) — the UI surfaces
 * those errors verbatim rather than pre-guessing them.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  LoadingState,
  Modal,
  MoneyText,
  StatCard,
  Textarea,
  useToast,
} from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { usePoll, useAsync, errorMessage } from '../../lib/hooks';
import { Chrome } from '../../components/Chrome';
import { todayIso, hhmm, stamp } from '../../lib/format';
import type {
  ApprovalCountResponse,
  ApprovalItem,
  ApprovalType,
  ApprovalsResponse,
  FloorMapResponse,
  OrderListResponse,
  StationLoadResponse,
  StationsResponse,
} from '../../lib/types';

export function ManagerShell({ onActivateDevice }: { readonly onActivateDevice: () => void }): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const branchId = me?.session.activeBranchId ?? null;

  const floor = usePoll<FloorMapResponse>(
    (signal) => api.org.tables(branchId ?? '', undefined, { signal }),
    10_000,
    [branchId],
    { enabled: branchId !== null },
  );

  const orders = usePoll<OrderListResponse>(
    (signal) => api.orders.list({ branchId: branchId ?? '', businessDate: todayIso(), limit: 200 }, { signal }),
    15_000,
    [branchId],
    { enabled: branchId !== null },
  );

  const stations = useAsync<StationsResponse>(
    (signal) => api.org.stations({ branchId: branchId ?? '', activeOnly: true }, { signal }),
    [branchId],
    { enabled: branchId !== null },
  );

  const tables = floor.data?.tables ?? [];

  const stats = useMemo(() => {
    const rows = orders.data?.items ?? [];
    const revenue = rows.reduce((acc, o) => (o.status === 'CANCELLED' || o.status === 'VOIDED' ? acc : acc + o.total_minor), 0);
    const outstanding = rows.reduce((acc, o) => acc + o.outstandingMinor, 0);
    return {
      orderCount: rows.length,
      revenue,
      outstanding,
      occupied: tables.filter((t) => t.session !== null).length,
      calling: tables.filter((t) => t.status === 'HELP_REQUESTED' || t.status === 'PAYMENT_REQUESTED').length,
      dirty: tables.filter((t) => t.status === 'NEEDS_CLEANING').length,
    };
  }, [orders.data, tables]);

  if (branchId === null) {
    return (
      <Chrome title="Quản lý ca" onActivateDevice={onActivateDevice}>
        <ErrorState title="Chưa chọn chi nhánh" message="Chọn chi nhánh trong menu tài khoản." />
      </Chrome>
    );
  }

  return (
    <Chrome title="Quản lý ca" onActivateDevice={onActivateDevice}>
      <div className="h-full space-y-4 overflow-y-auto p-4">
        {floor.initialLoading && orders.initialLoading ? <LoadingState label="Đang tải tổng quan…" /> : null}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Doanh thu hôm nay" value={<MoneyText amountMinor={stats.revenue} />} />
          <StatCard label="Đơn hôm nay" value={String(stats.orderCount)} />
          <StatCard label="Còn phải thu" value={<MoneyText amountMinor={stats.outstanding} />} />
          <StatCard
            label="Bàn đang phục vụ"
            value={`${stats.occupied}/${tables.length}`}
            sublabel={`${stats.calling} bàn đang gọi · ${stats.dirty} bàn chờ dọn`}
          />
        </div>

        <Card title="Tải các station" description="Cảnh báo ùn tắc bếp / bar theo thời gian thực.">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {(stations.data?.stations ?? []).map((station) => (
              <StationLoadCard key={station.id} stationId={station.id} name={station.name} />
            ))}
            {(stations.data?.stations ?? []).length === 0 ? (
              <EmptyState title="Không đọc được station" description="Cần quyền branch.view." />
            ) : null}
          </div>
        </Card>

        <Card title="Bàn cần chú ý">
          <ul className="divide-y divide-line">
            {tables
              .filter(
                (t) =>
                  t.status === 'HELP_REQUESTED' ||
                  t.status === 'PAYMENT_REQUESTED' ||
                  t.status === 'WAITING_CONFIRMATION' ||
                  t.status === 'NEEDS_CLEANING',
              )
              .map((table) => (
                <li key={table.id} className="flex items-center gap-2 py-2 text-sm">
                  <span className="font-semibold text-fg">{table.displayName}</span>
                  <Badge
                    tone={
                      table.status === 'PAYMENT_REQUESTED' ? 'danger' : table.status === 'HELP_REQUESTED' ? 'warning' : 'info'
                    }
                    size="sm"
                  >
                    {table.status}
                  </Badge>
                  <span className="ml-auto text-xs text-fg-subtle">
                    {table.session === null ? '' : `mở lúc ${hhmm(table.session.openedAt)}`}
                  </span>
                </li>
              ))}
          </ul>
        </Card>

        <ApprovalsInbox branchId={branchId} />
      </div>
    </Chrome>
  );
}

// ---------------------------------------------------------------------------
// Approvals inbox
// ---------------------------------------------------------------------------

const APPROVAL_TYPE_LABEL: Readonly<Record<ApprovalType, string>> = {
  PAYROLL_ADJUSTMENT: 'Điều chỉnh lương',
  SALARY_ADVANCE: 'Ứng lương',
  LEAVE_REQUEST: 'Xin nghỉ',
  SHIFT_SWAP: 'Đổi ca',
  STOCK_ADJUSTMENT: 'Điều chỉnh kho',
  PURCHASE_ORDER: 'Đơn mua hàng',
  STOCK_COUNT: 'Kiểm kê kho',
  KPI_APPEAL: 'Khiếu nại KPI',
  WASTAGE: 'Hao hụt / hàng hỏng',
};

interface PendingDecision {
  readonly item: ApprovalItem;
  readonly decision: 'approve' | 'reject';
}

function ApprovalsInbox({ branchId }: { readonly branchId: string }): React.JSX.Element {
  const api = useApi();
  const { permissions } = useAuth();
  const canView = permissions.has('approval.view');
  const canDecide = permissions.has('approval.decide');

  const count = usePoll<ApprovalCountResponse>(
    (signal) => api.approvals.count({ branchId }, { signal }),
    30_000,
    [branchId],
    { enabled: canView },
  );

  const list = usePoll<ApprovalsResponse>(
    (signal) => api.approvals.list({ branchId, status: 'PENDING' }, { signal }),
    30_000,
    [branchId],
    { enabled: canView },
  );

  const [pending, setPending] = useState<PendingDecision | null>(null);

  const grouped = useMemo(() => {
    const byType = new Map<ApprovalType, ApprovalItem[]>();
    for (const item of list.data?.approvals ?? []) {
      const bucket = byType.get(item.type);
      if (bucket === undefined) byType.set(item.type, [item]);
      else bucket.push(item);
    }
    return byType;
  }, [list.data]);

  if (!canView) {
    return (
      <Card title="Hộp thư duyệt">
        <p className="text-flow text-sm text-fg-muted">
          Tài khoản này không có quyền <code className="font-mono">approval.view</code> nên không xem được hàng chờ
          duyệt. Đăng nhập bằng tài khoản quản lý ca để dùng chức năng này.
        </p>
      </Card>
    );
  }

  const total = count.data?.total ?? 0;

  return (
    <Card
      title="Hộp thư duyệt"
      description="Ứng lương, xin nghỉ, đổi ca, kiểm kê, đơn mua hàng, hao hụt… — chỉ hiện những mục bạn đủ quyền quyết định."
      actions={
        <Badge tone={total > 0 ? 'warning' : 'success'} size="md">
          {total > 0 ? `${total} chờ duyệt` : 'Sạch hàng chờ'}
        </Badge>
      }
    >
      {list.initialLoading ? <LoadingState compact label="Đang tải hàng chờ duyệt…" /> : null}
      {list.error !== null && list.data === null ? (
        <ErrorState message={list.error.message} onRetry={list.reload} />
      ) : null}
      {list.data !== null && (list.data.approvals.length === 0) ? (
        <EmptyState title="Không có yêu cầu nào chờ duyệt" description="Danh sách tự làm mới mỗi 30 giây." />
      ) : null}

      <div className="space-y-4">
        {[...grouped.entries()].map(([type, items]) => (
          <section key={type}>
            <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-fg">
              {APPROVAL_TYPE_LABEL[type]}
              <Badge tone="neutral" size="sm">
                {items.length}
              </Badge>
            </h3>
            <ul className="divide-y divide-line">
              {items.map((item) => (
                <li key={`${item.type}-${item.id}`} className="flex flex-wrap items-center gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-flow text-sm font-medium text-fg">{item.titleVi}</p>
                    <p className="text-flow text-xs text-fg-muted">{item.summaryVi}</p>
                    <p className="text-xs text-fg-subtle">
                      {item.requestedBy?.name ?? 'Không rõ người yêu cầu'} · {stamp(item.requestedAt)}
                    </p>
                  </div>
                  {item.amountMinor !== null ? (
                    <MoneyText amountMinor={item.amountMinor} className="text-sm font-semibold" />
                  ) : null}
                  <Button
                    size="sm"
                    variant="success"
                    disabled={!canDecide}
                    onClick={() => setPending({ item, decision: 'approve' })}
                  >
                    Duyệt
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!canDecide}
                    onClick={() => setPending({ item, decision: 'reject' })}
                  >
                    Từ chối
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {!canDecide ? (
        <p className="text-flow mt-3 text-xs text-fg-subtle">
          Bạn xem được hàng chờ nhưng thiếu quyền <code className="font-mono">approval.decide</code> để quyết định.
        </p>
      ) : null}

      {pending !== null ? (
        <DecisionSheet
          pending={pending}
          onClose={() => setPending(null)}
          onDecided={() => {
            setPending(null);
            list.reload();
            count.reload();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * The reason sheet. Both directions REQUIRE a reason (`x-action-reason`) —
 * "vì sao được duyệt" is audited exactly like "vì sao bị từ chối". An accepted
 * KPI appeal must additionally carry a non-zero points adjustment
 * (`adjustmentPointsMilli`), so that field appears only for that one case.
 */
function DecisionSheet({
  pending,
  onClose,
  onDecided,
}: {
  readonly pending: PendingDecision;
  readonly onClose: () => void;
  readonly onDecided: () => void;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const { item, decision } = pending;
  const approving = decision === 'approve';
  const needsPoints = approving && item.type === 'KPI_APPEAL';

  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [points, setPoints] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError('Lý do phải có ít nhất 3 ký tự.');
      return;
    }
    // Points are entered as whole points and stored as MILLI-points on the
    // wire: '1.5' -> 1500, converted by string manipulation (see parse below)
    // so no float rounding can shave a person's KPI.
    let adjustmentPointsMilli: number | undefined;
    if (needsPoints) {
      const parsed = parsePointsToMilli(points.trim());
      if (parsed === null || parsed === 0) {
        setError('Khiếu nại KPI được chấp nhận phải kèm số điểm điều chỉnh khác 0 (ví dụ 1.5 hoặc -2).');
        return;
      }
      adjustmentPointsMilli = parsed;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        note: note.trim() === '' ? null : note.trim(),
        ...(adjustmentPointsMilli === undefined ? {} : { adjustmentPointsMilli }),
      };
      const result = approving
        ? await api.approvals.approve(item.type, item.id, trimmed, body)
        : await api.approvals.reject(item.type, item.id, trimmed, body);
      toast.success(
        approving ? `Đã duyệt: ${item.titleVi}` : `Đã từ chối: ${item.titleVi}`,
        `Trạng thái mới: ${result.status}`,
      );
      onDecided();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${approving ? 'Duyệt' : 'Từ chối'}: ${item.titleVi}`}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Huỷ
          </Button>
          <Button variant={approving ? 'success' : 'danger'} loading={busy} onClick={() => void submit()}>
            {approving ? 'Xác nhận duyệt' : 'Xác nhận từ chối'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-flow text-sm text-fg-muted">{item.summaryVi}</p>
        <FormField label={approving ? 'Lý do duyệt' : 'Lý do từ chối'} required>
          {(field) => (
            <Textarea {...field} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} required />
          )}
        </FormField>
        <FormField label="Ghi chú thêm (tuỳ chọn)">
          {(field) => <Textarea {...field} value={note} onChange={(e) => setNote(e.target.value)} rows={2} />}
        </FormField>
        {needsPoints ? (
          <FormField label="Điểm điều chỉnh (dương = cộng, âm = trừ)" required hint="Ví dụ: 1.5 hoặc -2">
            {(field) => (
              <Input {...field} value={points} onChange={(e) => setPoints(e.target.value)} inputMode="decimal" required />
            )}
          </FormField>
        ) : null}
        {error !== null ? <ErrorState title="Không thực hiện được" message={error} /> : null}
        <p className="text-flow text-xs text-fg-subtle">
          Quyết định được ghi nhật ký kèm lý do. Máy chủ từ chối tự duyệt yêu cầu của chính mình (nguyên tắc bất kiêm
          nhiệm).
        </p>
      </div>
    </Modal>
  );
}

/**
 * '1.5' -> 1500 milli-points by STRING manipulation, never `parseFloat * 1000`
 * (0.1 * 1000 === 100.00000000000001 territory). Accepts ',' as the decimal
 * separator because vi-VN keyboards produce it.
 */
function parsePointsToMilli(input: string): number | null {
  const match = /^(-?)(\d+)(?:[.,](\d{1,3}))?$/.exec(input);
  if (match === null) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const whole = Number.parseInt(match[2] ?? '0', 10);
  const fraction = Number.parseInt(((match[3] ?? '') + '000').slice(0, 3), 10);
  return sign * (whole * 1000 + fraction);
}

function StationLoadCard({ stationId, name }: { readonly stationId: string; readonly name: string }): React.JSX.Element {
  const api = useApi();
  const load = usePoll<StationLoadResponse>(
    (signal) => api.production.load(stationId, { signal }),
    15_000,
    [stationId],
  );

  if (load.data === null) return <Card title={name}>{load.initialLoading ? <LoadingState compact /> : null}</Card>;

  const data = load.data;
  return (
    <Card
      title={name}
      className={data.isOverloaded ? 'ring-2 ring-danger-solid' : undefined}
      actions={
        <Badge tone={data.isOverloaded ? 'danger' : data.lateCount > 0 ? 'warning' : 'success'}>
          {data.isOverloaded ? 'Quá tải' : data.lateCount > 0 ? 'Có món trễ' : 'Bình thường'}
        </Badge>
      }
    >
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <Metric label="Chờ làm" value={data.waitingCount} />
        <Metric label="Đang làm" value={data.inProgressCount} />
        <Metric label="Chờ lấy" value={data.waitingPickupCount} />
        <Metric label="Trễ" value={data.lateCount} />
      </dl>
      <p className="mt-2 text-xs text-fg-subtle">
        Tải {Math.round(data.loadRatioBp / 100)}% · chờ lâu nhất {Math.round(data.oldestWaitingSeconds / 60)} phút ·{' '}
        {data.activeStaffCount} người đang làm
      </p>
    </Card>
  );
}

function Metric({ label, value }: { readonly label: string; readonly value: number }): React.JSX.Element {
  return (
    <div>
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="text-lg font-bold text-fg sa-tabular">{value}</dd>
    </div>
  );
}
